import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { LoggerService } from './logger.service';

/**
 * Audible + haptic confirmation for hands-free workflows (e.g. a patrol guard
 * who scans a checkpoint and never looks at the screen). Used by the
 * `play_success_sound` form option.
 *
 * The chime is synthesized with Web Audio, so there's no audio asset to ship
 * or cache. Browsers only let an AudioContext produce sound after a user
 * gesture, so the context is created and resumed on the first pointer/key
 * event anywhere in the app ({@link installUnlockListeners}); a save that
 * happens later (even one with no tap of its own, like an auto-submitted form
 * opened by a scan) can then play. A launch straight from an NFC tag with no
 * interaction at all may still be silent on the web; vibration still fires.
 */
@Injectable({ providedIn: 'root' })
export class FeedbackSoundService {
  private document = inject(DOCUMENT);
  private log = inject(LoggerService).getLogger('feedback-sound');
  private context: AudioContext | null = null;
  private unlockInstalled = false;

  private readonly unlock = () => {
    const ctx = this.getContext();
    if (ctx && ctx.state === 'suspended') {
      void ctx.resume().catch(() => undefined);
    }
    if (ctx?.state === 'running') {
      this.removeUnlockListeners();
    }
  };

  /** Registers one-time gesture listeners that unlock audio. Safe to call repeatedly. */
  installUnlockListeners(): void {
    if (this.unlockInstalled) return;
    this.unlockInstalled = true;
    this.document.addEventListener('pointerdown', this.unlock, { capture: true });
    this.document.addEventListener('keydown', this.unlock, { capture: true });
  }

  /** Plays a short rising two-note chime and a short vibration. Never throws. */
  async playSuccess(): Promise<void> {
    this.vibrate([60, 40, 60]);

    const ctx = this.getContext();
    if (!ctx) return;
    try {
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      const start = ctx.currentTime;
      this.tone(ctx, 880, start, 0.12);
      this.tone(ctx, 1320, start + 0.13, 0.18);
    } catch (err) {
      this.log.debug('Success sound unavailable:', err);
    }
  }

  private tone(ctx: AudioContext, frequency: number, at: number, duration: number): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = frequency;
    // Quick attack and exponential release so the notes don't click.
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.3, at + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    osc.connect(gain).connect(ctx.destination);
    osc.start(at);
    osc.stop(at + duration + 0.02);
  }

  private vibrate(pattern: number[]): void {
    try {
      this.document.defaultView?.navigator.vibrate?.(pattern);
    } catch {
      // Unsupported or blocked -- the sound is the primary signal.
    }
  }

  private getContext(): AudioContext | null {
    if (this.context) return this.context;
    const win = this.document.defaultView as {
      AudioContext?: typeof AudioContext;
      webkitAudioContext?: typeof AudioContext;
    } | null;
    const Ctor = win?.AudioContext ?? win?.webkitAudioContext;
    if (!Ctor) return null;
    try {
      this.context = new Ctor();
    } catch (err) {
      this.log.debug('AudioContext unavailable:', err);
      return null;
    }
    return this.context;
  }

  private removeUnlockListeners(): void {
    this.document.removeEventListener('pointerdown', this.unlock, { capture: true });
    this.document.removeEventListener('keydown', this.unlock, { capture: true });
  }
}
